/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// One-shot reverse geocode used at vibe-drop time so the active-vibe card
// displays the address where the vibe was dropped instead of the literal
// string "Current location". Never call this on a render path — each call
// hits the Google Maps billing meter.

const PREFERRED_TYPES = new Set([
  "point_of_interest",
  "establishment",
  "premise",
  "street_address",
]);

export async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  if (typeof window === "undefined") return null;
  const Geocoder = window.google?.maps?.Geocoder;
  if (!Geocoder) return null;

  try {
    const geocoder = new Geocoder();
    const { results } = await geocoder.geocode({ location: { lat, lng } });
    if (!results || results.length === 0) return null;

    const best =
      results.find((r) => r.types?.some((t) => PREFERRED_TYPES.has(t))) ?? results[0];
    return best.formatted_address ?? null;
  } catch {
    return null;
  }
}

export function coordinateFallbackLabel(lat: number, lng: number): string {
  return `Dropped at ${lat.toFixed(4)}, ${lng.toFixed(4)}`;
}

// City-only reverse geocode for the SafeCircle MockFriend mirror.
// Returns "Locality, Administrative Area" (e.g. "Mountain View,
// California") by picking out address_components instead of the raw
// formatted_address. Falls back to null when google.maps isn't loaded
// yet or the geocoder returns nothing useful.
export async function reverseGeocodeCity(
  lat: number,
  lng: number,
): Promise<string | null> {
  if (typeof window === "undefined") return null;
  const Geocoder = window.google?.maps?.Geocoder;
  if (!Geocoder) return null;

  try {
    const geocoder = new Geocoder();
    const { results } = await geocoder.geocode({ location: { lat, lng } });
    if (!results || results.length === 0) return null;

    // Search each result top-down for a locality + admin_area_level_1 pair.
    for (const r of results) {
      const comps = r.address_components ?? [];
      const locality = comps.find((c) =>
        c.types?.includes("locality"),
      )?.long_name;
      const admin = comps.find((c) =>
        c.types?.includes("administrative_area_level_1"),
      )?.long_name;
      if (locality && admin) return `${locality}, ${admin}`;
      if (locality) return locality;
    }
    // Final fallback — strip the postal code + country off the
    // formatted_address so we don't leak a precise street address into a
    // social tile.
    const first = results[0]?.formatted_address ?? null;
    if (!first) return null;
    const parts = first.split(",").map((p) => p.trim());
    // "1600 Amphitheatre Pkwy, Mountain View, CA 94043, USA" → keep idx 1
    return parts[1] ?? first;
  } catch {
    return null;
  }
}
